import { useCallback, useState, type RefObject } from 'react';

import { toast } from '@commenda/kakashi';
import { useReport } from '../context/ReportContext';
import { BreakdownListHelper } from '../helpers/BreakdownListHelper';
import { ColumnHeaderHelper } from '../helpers/ColumnHeaderHelper';
import { MetricsHelper } from '../helpers/MetricsHelper';
import { PeriodHelper } from '../helpers/PeriodHelper';
import { ReportComponentHelper } from '../helpers/ReportComponentHelper';
import { ReportExportHelper } from '../helpers/ReportExportHelper';
import { TableModelHelper } from '../helpers/TableModelHelper';
import type { PeriodOverride } from '../types/period';
import type { IComponentFacts } from '../types/report';
import type { ITableModel } from '../types/tables';

interface IExportTable {
  title: string;
  model: ITableModel;
}

interface IExportDefinition {
  term: string;
  definition: string;
}

/** PDF (canvas snapshot) and XLSX (fact table + every table component) exports for the current report. */
export const useReportExports = (
  canvasRef: RefObject<HTMLDivElement | null>
) => {
  const {
    dataset,
    table,
    filter,
    items: allItems,
    currency,
    booksClosedThrough,
    columnLayout,
  } = useReport();
  const items = ReportComponentHelper.visibleItems(allItems, columnLayout);
  const [isExporting, setIsExporting] = useState(false);

  const resolve = useCallback(
    (override?: PeriodOverride | null) => {
      const effective = PeriodHelper.mergeFilter(filter, override);
      return {
        effective,
        resolved: PeriodHelper.resolve(
          effective,
          booksClosedThrough,
          table.fiscalYearStartMonth
        ),
      };
    },
    [filter, booksClosedThrough, table.fiscalYearStartMonth]
  );

  const exportXlsx = useCallback(() => {
    const { resolved } = resolve();
    const tables: IExportTable[] = [];
    const definitions = new Map<string, IExportDefinition>();
    items.forEach((item) => {
      const { effective, resolved: itemResolved } = resolve(item.override);
      const facts: IComponentFacts = {
        ...MetricsHelper.compare(table, itemResolved),
        table,
        resolved: itemResolved,
        filter: effective,
        currency,
        hasOverride: Boolean(item.override),
      };
      const { title } = ReportComponentHelper.definition(item.componentId);
      const model = TableModelHelper.forComponent(item.componentId, facts);
      if (model) tables.push({ title, model });

      const list = BreakdownListHelper.listIdFor(item.componentId);
      if (list) {
        tables.push({
          title,
          model: BreakdownListHelper.toTableModel(
            BreakdownListHelper.model(list, facts),
            itemResolved.comparisonLabel
          ),
        });
      }

      const column = ColumnHeaderHelper.columnIdFor(item.componentId);
      if (column) {
        ColumnHeaderHelper.model(column, facts).rows.forEach((row) =>
          definitions.set(row.label, {
            term: row.label,
            definition: row.definition,
          })
        );
      }
    });
    ReportExportHelper.downloadXlsx({
      companyName: dataset.company.name,
      currency,
      resolved,
      booksClosedThrough,
      table,
      tables,
      definitions: Array.from(definitions.values()),
    });
  }, [
    resolve,
    items,
    table,
    currency,
    dataset.company.name,
    booksClosedThrough,
  ]);

  const exportPdf = useCallback(async () => {
    if (!canvasRef.current) return;
    const { resolved } = resolve();
    setIsExporting(true);
    try {
      await ReportExportHelper.downloadPdf({
        companyName: dataset.company.name,
        periodLabel: resolved.label,
        comparisonLabel: resolved.comparisonLabel,
        booksClosedThrough,
        root: canvasRef.current,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'PDF export failed');
    } finally {
      setIsExporting(false);
    }
  }, [canvasRef, resolve, dataset.company.name, booksClosedThrough]);

  return { exportPdf, exportXlsx, isExporting };
};
